import { FunctionComponent, Fragment } from 'react';
import { FlexContainer } from '@/components/Layout/Container';
import {
    IntegrationConfiguration,
    ProductConfiguration,
} from '@/models/product';
import { Box, styled, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { formatStarjackDate } from '@starjack/utils';
import { sub, getYear } from 'date-fns';

const getDisplayDate = (date: Date) => {
    return getYear(date) === 2100 ? sub(date, { years: 1 }) : date;
};

const configKeyTranslation = {
    barcode: 'E-Ticket (Zutritt mit Barcode / QR-Code-Scanner)',
    internalProduction: 'Produktion und Versand durch die Bergbahn',
    keycard: 'Keycard',
    pickup: 'Pick-up-Voucher (Abholung Automat oder Kassa)',
    mobileFlow: 'Smartphone Ticket',
};

const configurationHasActiavtedKey = (
    configurartion: IntegrationConfiguration
) => Object.values(configurartion).some((c) => c);

export const ProductConfigurationOverview: FunctionComponent<
    React.PropsWithChildren<{
        productConfiguration: ProductConfiguration;
    }>
> = ({ productConfiguration }) => {
    const {
        integrationConfiguration,
        portalConfiguration,
        b2bConfiguration,
        hospitalityConfiguration,
    } = productConfiguration;

    return (
        <FlexContainer
            sx={{
                width: 480,
            }}
            justifyContent='center'
            column
        >
            {productConfiguration.enabled ? (
                <Row>
                    <CheckIcon />
                    Produkt aktiv zum Kauf
                </Row>
            ) : (
                <Row>
                    <CloseIcon />
                    Produkt inaktiv
                </Row>
            )}

            {productConfiguration.startDateValidityRange.to &&
                productConfiguration.startDateValidityRange.from && (
                    <Row>
                        <CheckIcon />
                        Gültigkeitszeitraum:{' '}
                        {formatStarjackDate(
                            productConfiguration.startDateValidityRange.from
                        )}{' '}
                        -{' '}
                        {formatStarjackDate(
                            getDisplayDate(
                                productConfiguration.startDateValidityRange.to
                            )
                        )}
                    </Row>
                )}

            {productConfiguration.validityRange.to &&
                productConfiguration.validityRange.from && (
                    <Row>
                        <CheckIcon />
                        Verkaufszeitraum:{' '}
                        {formatStarjackDate(
                            productConfiguration.validityRange.from
                        )}{' '}
                        -{' '}
                        {formatStarjackDate(
                            getDisplayDate(
                                productConfiguration.validityRange.to
                            )
                        )}
                    </Row>
                )}

            {!!productConfiguration.maxTicketsPerStartDay && (
                <Row gutterBottom>
                    <CheckIcon />
                    Kontingent aktiv
                </Row>
            )}

            <Row>
                <CheckIcon />
                Aktivierte Buchungsarten in Verkaufskanälen:
            </Row>
            <Box
                sx={{
                    ml: 1,
                }}
            >
                {configurationHasActiavtedKey(portalConfiguration) && (
                    <BookingMethod
                        configurationTitle='starjack.at & starjack App:'
                        integrationConfiguration={portalConfiguration}
                    />
                )}

                {configurationHasActiavtedKey(integrationConfiguration) && (
                    <BookingMethod
                        configurationTitle='Webshop auf meiner Webseite:'
                        integrationConfiguration={integrationConfiguration}
                    />
                )}

                {configurationHasActiavtedKey(b2bConfiguration) && (
                    <BookingMethod
                        configurationTitle='starjack.B2B:'
                        integrationConfiguration={b2bConfiguration}
                    />
                )}

                {configurationHasActiavtedKey(hospitalityConfiguration) && (
                    <BookingMethod
                        configurationTitle='Hospitality:'
                        integrationConfiguration={hospitalityConfiguration}
                    />
                )}
            </Box>
        </FlexContainer>
    );
};

const CheckIcon = styled(CheckCircleIcon)(({ theme }) => ({
    color: theme.palette.success.main,
    marginRight: theme.spacing(2),
}));

const CloseIcon = styled(HighlightOffIcon)(({ theme }) => ({
    color: theme.palette.error.main,
    marginRight: theme.spacing(2),
}));

const Row = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
}));

const BookingMethod: FunctionComponent<
    React.PropsWithChildren<{
        configurationTitle: string;
        integrationConfiguration: IntegrationConfiguration;
    }>
> = ({ configurationTitle, integrationConfiguration }) => {
    const activatedKeys = Object.keys(integrationConfiguration).filter(
        (c) => integrationConfiguration[c]
    );
    return (
        <FlexContainer
            sx={{
                width: '100%',
                ml: 8,
                mb: 2,
            }}
            column
        >
            <Typography variant='subtitle2'>{configurationTitle}</Typography>
            <FlexContainer
                sx={{
                    display: 'flex',
                    width: '100%',
                    flexWrap: 'wrap',
                    ml: 2,
                }}
            >
                {activatedKeys.map((key, i) => (
                    <Fragment key={i}>
                        <Typography>{configKeyTranslation[key]}</Typography>
                        {i < activatedKeys.length - 1 && (
                            <Typography>,&nbsp;</Typography>
                        )}
                    </Fragment>
                ))}
            </FlexContainer>
        </FlexContainer>
    );
};
